import { useCallback, useState } from 'react';

import {
	GRID_PLACEMENT_ATTEMPT_LIMIT,
	getGridPositionColorSeed,
	isBlockedBySafeArea,
	isPositionTooCloseToLetters,
	placeAvailableGridPosition,
	reserveGridArea,
	reserveSnakeLeaderBuffer,
	reserveSnakePositions,
} from '@/features/letterParty/gridPlacement';

import type { Dispatch, SetStateAction } from 'react';
import type { Question, QuestionPart } from '@/features/gameSession/types';
import type { ConfettiCollectible, HatCollectible, LetterQ, Position } from '@/features/letterParty/types';

interface UseQuestionSetupProps {
	questions: Question[];
	snake: Position[];
	gridWidth: number;
	gridHeight: number;
	safeAreaWidth: number;
	safeAreaHeight: number;
	onResetSnake: () => void;
	onResetGameState: () => void;
	onResetWordProgress: () => void;
	onPauseSnake: () => void;
}

interface UseQuestionSetupReturn {
	currentWord: string;
	currentAnswerParts: LetterPartyAnswerPart[];
	currentQuestionIndex: number;
	letterQs: LetterQ[];
	hats: HatCollectible[];
	confettis: ConfettiCollectible[];
	setLetterQs: Dispatch<SetStateAction<LetterQ[]>>;
	setHats: Dispatch<SetStateAction<HatCollectible[]>>;
	setConfettis: Dispatch<SetStateAction<ConfettiCollectible[]>>;
	startNewWord: (preserveScore?: boolean, preserveSnake?: boolean) => void;
	initGame: () => void;
	isLastQuestion: boolean;
}

interface LetterPartyAnswerPart extends Omit<QuestionPart, 'text'> {
	text: string;
}

/**
 * Custom hook to manage question setup for Letter Party game
 *
 * Handles:
 * - Question tracking and word extraction
 * - Correct letter placement from word
 * - Decoy letter placement from backend response
 * - Hat collectible placement
 * - Safe area management (avoiding snake and reserved zones)
 * - Game progression through questions
 */
export function useQuestionSetup({
	questions,
	snake,
	gridWidth,
	gridHeight,
	safeAreaWidth,
	safeAreaHeight,
	onResetSnake,
	onResetGameState,
	onResetWordProgress,
	onPauseSnake,
}: UseQuestionSetupProps): UseQuestionSetupReturn {
	const [currentWord, setCurrentWord] = useState('');
	const [currentAnswerParts, setCurrentAnswerParts] = useState<LetterPartyAnswerPart[]>([]);
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const [letterQs, setLetterQs] = useState<LetterQ[]>([]);
	const [hats, setHats] = useState<HatCollectible[]>([]);
	const [confettis, setConfettis] = useState<ConfettiCollectible[]>([]);

	/**
	 * Calculate safe areas where collectibles should not spawn
	 */
	const calculateSafeAreas = useCallback(
		(preserveSnake: boolean): Set<string> => {
			const usedPositions = new Set<string>();

			reserveGridArea(usedPositions, {
				width: safeAreaWidth,
				height: safeAreaHeight,
			});

			reserveSnakePositions(usedPositions, snake);

			if (preserveSnake && snake.length > 0) {
				reserveSnakeLeaderBuffer(usedPositions, snake);
			}

			return usedPositions;
		},
		[snake, safeAreaWidth, safeAreaHeight],
	);

	/**
	 * Place correct letters from the word on the grid
	 */
	const placeCorrectLetters = useCallback(
		(answerParts: LetterPartyAnswerPart[], usedPositions: Set<string>): LetterQ[] => {
			const positions: LetterQ[] = [];

			answerParts.forEach(({ text }) => {
				const position = placeAvailableGridPosition({
					gridWidth,
					gridHeight,
					usedPositions,
					maxAttempts: GRID_PLACEMENT_ATTEMPT_LIMIT,
					isPositionBlocked: (candidate) =>
						isBlockedBySafeArea(candidate) || isPositionTooCloseToLetters(candidate, positions),
				});

				if (!position) {
					return;
				}

				positions.push({
					position,
					letter: text,
					collected: false,
					colorSeed: getGridPositionColorSeed(position),
				});
			});

			return positions;
		},
		[gridWidth, gridHeight],
	);

	/**
	 * Place decoy letters from backend response
	 */
	const placeDecoyLetters = useCallback(
		(usedPositions: Set<string>, backendDecoyLetters?: string[], existingLetters?: LetterQ[]): LetterQ[] => {
			if (!backendDecoyLetters || backendDecoyLetters.length === 0) {
				return [];
			}

			const decoyLetters = backendDecoyLetters.map((letter) => letter.toLowerCase());
			const positions: LetterQ[] = [];
			const allPlacedLetters = [...(existingLetters || [])];

			// Place decoy letters
			decoyLetters.forEach((letter) => {
				const position = placeAvailableGridPosition({
					gridWidth,
					gridHeight,
					usedPositions,
					maxAttempts: GRID_PLACEMENT_ATTEMPT_LIMIT,
					isPositionBlocked: (candidate) =>
						isBlockedBySafeArea(candidate) || isPositionTooCloseToLetters(candidate, allPlacedLetters),
				});

				if (!position) {
					return;
				}

				const newLetter = {
					position,
					letter,
					collected: false,
					colorSeed: getGridPositionColorSeed(position),
				};
				positions.push(newLetter);
				allPlacedLetters.push(newLetter);
			});

			return positions;
		},
		[gridWidth, gridHeight],
	);

	/**
	 * Place hat collectibles on the grid
	 */
	const placeHats = useCallback(
		(usedPositions: Set<string>): HatCollectible[] => {
			// Place 1-2 hats randomly per word
			const numHats = 1 + Math.floor(Math.random() * 2);
			const hatPositions: HatCollectible[] = [];

			for (let i = 0; i < numHats; i++) {
				const position = placeAvailableGridPosition({
					gridWidth,
					gridHeight,
					usedPositions,
					maxAttempts: GRID_PLACEMENT_ATTEMPT_LIMIT,
					isPositionBlocked: isBlockedBySafeArea,
				});

				if (!position) {
					return hatPositions;
				}

				hatPositions.push({
					position,
					collected: false,
					id: i,
				});
			}

			return hatPositions;
		},
		[gridWidth, gridHeight],
	);

	/**
	 * Place confetti collectibles on the grid
	 */
	const placeConfettis = useCallback(
		(usedPositions: Set<string>): ConfettiCollectible[] => {
			const confettiPositions: ConfettiCollectible[] = [];

			const position = placeAvailableGridPosition({
				gridWidth,
				gridHeight,
				usedPositions,
				maxAttempts: GRID_PLACEMENT_ATTEMPT_LIMIT,
				isPositionBlocked: isBlockedBySafeArea,
			});

			if (!position) {
				return confettiPositions;
			}

			confettiPositions.push({
				position,
				collected: false,
				id: 0,
			});

			return confettiPositions;
		},
		[gridWidth, gridHeight],
	);

	/**
	 * Start a new word/question
	 */
	const startNewWord = useCallback(
		(preserveScore: boolean = false, preserveSnake: boolean = false) => {
			// Get current question from questions array
			const questionIndex = preserveScore ? currentQuestionIndex + 1 : 0;
			const currentQuestion = questions[questionIndex];

			// If no question available, handle gracefully
			if (!currentQuestion) {
				console.warn('Letter Party - No question available at index:', questionIndex);
				return;
			}

			if (typeof currentQuestion.question_display !== 'string') {
				console.warn('Letter Party - question_display is not a string:', currentQuestion.question_display);
				return;
			}

			const word = currentQuestion.question_display.trim().toLowerCase();
			let answerParts: LetterPartyAnswerPart[];
			if (currentQuestion.question_partial !== undefined) {
				if (
					!Array.isArray(currentQuestion.question_partial) ||
					currentQuestion.question_partial.length === 0 ||
					currentQuestion.question_partial.some(
						({ text }) => !['string', 'number'].includes(typeof text) || String(text).length === 0,
					)
				) {
					console.warn('Letter Party - question_partial must contain non-empty text or number parts');
					return;
				}

				answerParts = currentQuestion.question_partial.map(({ text, audio_url }) => ({
					text: String(text).toLowerCase(),
					audio_url,
				}));
			} else {
				answerParts = word.split('').map((text) => ({ text }));
			}

			if (answerParts.map(({ text }) => text).join('') !== word) {
				console.warn('Letter Party - question_partial does not match question_display');
				return;
			}

			setCurrentWord(word);
			setCurrentAnswerParts(answerParts);

			// Always reset word progress (letters collected/revealed) for new word
			onResetWordProgress();

			// Pause snake when starting next question (wait for player input)
			if (preserveScore && preserveSnake) {
				onPauseSnake();
			}

			// Reset snake position and direction if not preserving
			if (!preserveSnake) {
				onResetSnake();
			}

			// Calculate safe areas for placement
			const usedPositions = calculateSafeAreas(preserveSnake);

			// Place all collectibles
			const correctLetters = placeCorrectLetters(answerParts, usedPositions);
			const decoyLetters = placeDecoyLetters(usedPositions, currentQuestion.decoy_letters, correctLetters);
			const hatPositions = placeHats(usedPositions);
			const confettiPositions = placeConfettis(usedPositions);

			setLetterQs([...correctLetters, ...decoyLetters]);
			setHats(hatPositions);
			setConfettis(confettiPositions);

			// Update question index
			setCurrentQuestionIndex(questionIndex);

			// Reset game state if starting fresh
			if (!preserveScore) {
				onResetGameState();
			}
		},
		[
			currentQuestionIndex,
			questions,
			onResetSnake,
			onResetGameState,
			onResetWordProgress,
			onPauseSnake,
			calculateSafeAreas,
			placeCorrectLetters,
			placeDecoyLetters,
			placeHats,
			placeConfettis,
		],
	);

	/**
	 * Initialize game (resets to first question)
	 */
	const initGame = useCallback(() => {
		startNewWord(false, false);
	}, [startNewWord]);

	const isLastQuestion = currentQuestionIndex >= questions.length - 1;

	return {
		currentWord,
		currentAnswerParts,
		currentQuestionIndex,
		letterQs,
		hats,
		confettis,
		setLetterQs,
		setHats,
		setConfettis,
		startNewWord,
		initGame,
		isLastQuestion,
	};
}
